import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { PickupMan } from "./PickupMan";
import { commonButtonProps } from "../../../utils/styles";
import { commonTextFieldProps } from "../../../utils/styles";

interface AddPickupManModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (pickupMan: any) => void;
  pickupMan?: PickupMan;
  isEdit?: boolean;
}

const commissionTypes = ["Percentage", "Fixed"];

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must not exceed 50 characters"),
  contactNumber: Yup.string()
    .required("Contact number is required")
    .matches(/^[0-9]+$/, "Contact number must contain only digits")
    .min(10, "Contact number must be exactly 10 digits")
    .max(10, "Contact number must be exactly 10 digits"),
  commissionType: Yup.string()
    .nullable()
    .oneOf(["Percentage", "Fixed"], "Invalid commission type"),
  commissionValue: Yup.number()
    .nullable()
    .when("commissionType", {
      is: (commissionType: string) => commissionType === "Percentage",
      then: (schema) => schema.max(100, "Percentage cannot exceed 100"),
      otherwise: (schema) =>
        schema.max(999999, "Commission cannot exceed 999,999"),
    })
    .positive("Commission value must be a positive number"),
  location: Yup.string()
    .nullable()
    .max(200, "Location must not exceed 200 characters"),
});

const initialValues: Omit<PickupMan, "id"> = {
  name: "",
  contactNumber: "",
  commissionType: null,
  commissionValue: null,
  location: "",
  companyId: "",
};

const AddPickupManModal: React.FC<AddPickupManModalProps> = ({
  open,
  onClose,
  onSubmit,
  pickupMan,
  isEdit = false,
}) => {
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;

  const handleSubmit = (values: any) => {
    const enrichedValues = {
      ...values,
      // Convert commissionValue to number if it's not empty, otherwise null
      commissionValue:
        values.commissionValue !== "" && values.commissionValue !== null
          ? Number(values.commissionValue)
          : null,
      ...(!isEdit && { companyId: companyId }),
    };
    onSubmit(enrichedValues);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Update Pickup Man" : "Add New Pickup Man"}
      </DialogTitle>
      <Formik
        initialValues={pickupMan || initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, isValid, dirty, setFieldValue }) => (
          <Form>
            <DialogContent sx={{ p: 2, pt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Name"
                    name="name"
                    value={values.name}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Contact Number"
                    name="contactNumber"
                    value={values.contactNumber}
                    error={
                      touched.contactNumber && Boolean(errors.contactNumber)
                    }
                    helperText={touched.contactNumber && errors.contactNumber}
                    inputProps={{
                      maxLength: 10,
                    }}
                    InputProps={{
                      inputMode: "numeric",
                    }}
                    placeholder="Enter 10 digit number"
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    select
                    fullWidth
                    label="Commission Type"
                    name="commissionType"
                    value={values.commissionType || ""}
                    error={
                      touched.commissionType && Boolean(errors.commissionType)
                    }
                    helperText={touched.commissionType && errors.commissionType}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue("commissionType", e.target.value || null);
                      if (!e.target.value) {
                        setFieldValue("commissionValue", null);
                      }
                    }}
                    {...commonTextFieldProps}
                  >
                    <MenuItem value="">Select Type</MenuItem>
                    {commissionTypes.map((type) => (
                      <MenuItem key={type} value={type} sx={{ fontSize: "0.75rem" }}>
                        {type}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label={`Commission Value ${
                      values.commissionType === "Percentage"
                        ? "(%)"
                        : values.commissionType === "Fixed"
                        ? "(₹)"
                        : ""
                    }`}
                    name="commissionValue"
                    type="number"
                    value={values.commissionValue}
                    error={
                      touched.commissionValue && Boolean(errors.commissionValue)
                    }
                    helperText={
                      touched.commissionValue && errors.commissionValue
                    }
                    placeholder={
                      values.commissionType === "Percentage"
                        ? "Enter percentage"
                        : "Enter amount"
                    }
                    InputProps={{
                      inputMode: "decimal",
                    }}
                    disabled={!values.commissionType}
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Location"
                    name="location"
                    multiline
                    rows={3}
                    error={touched.location && Boolean(errors.location)}
                    helperText={touched.location && errors.location}
                    placeholder="Enter location details"
                    value={values.location}
                    {...commonTextFieldProps}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} {...commonButtonProps}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!(isValid && dirty)}
                {...commonButtonProps}
              >
                {isEdit ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddPickupManModal;
